import { useEffect, useRef } from 'react'
import {
  trackRegistrationStep,
  RegistrationStepData,
} from '../../../utils/analytics'

interface UseRegistrationStepTrackingParams {
  /** Current step slug, taken from the URL (the source of truth for the step). */
  stepSlug?: string
  /** Registrant attributes to attach to the event. IDs/enums only, no PII. */
  data?: RegistrationStepData
}

/**
 * Emits one `registration_step` analytics event per step the user lands on.
 *
 * Watches the URL slug rather than wrapping the context's `setStep`, because
 * navigation happens through several paths — `setStep`/`goBack` in
 * RegistrationContext, plus `history.replace` in index.jsx (server-step
 * reconciliation on load, post-signup redirect) and Main.jsx (bad-slug
 * self-heal). Instrumenting the URL covers all of them, including browser
 * back/forward, deep links and refreshes, with nothing to remember at future
 * call sites.
 *
 * Re-renders are deduped against the last tracked slug, so an event fires only
 * when the step genuinely changes — not on remount or unrelated context
 * updates.
 */
export const useRegistrationStepTracking = ({
  stepSlug,
  data,
}: UseRegistrationStepTrackingParams): void => {
  'use memo'

  // Last slug we emitted an event for. Doubles as the `from_step` of the next
  // event, so back-navigation reports the step actually left (e.g. waiver ->
  // review) rather than the forward-only path.
  const lastTrackedSlug = useRef<string | undefined>(undefined)

  // Read the latest attributes without making them an effect dependency: they
  // arrive asynchronously (division, role) and would otherwise re-fire the
  // event for the same step each time one resolves.
  const dataRef = useRef<RegistrationStepData | undefined>(data)
  dataRef.current = data

  useEffect(() => {
    if (!stepSlug || stepSlug === lastTrackedSlug.current) {
      return
    }

    const fromStep = lastTrackedSlug.current
    lastTrackedSlug.current = stepSlug

    trackRegistrationStep(stepSlug, fromStep, dataRef.current ?? {})
  }, [stepSlug])
}
